import { EvaluatedPricingLine, PricingLinePhase } from "@forgepoint/shared";
import { PriceRow } from "./PriceRow";

interface PricingLineRowsProps {
  /** Per-quantity price data, each carrying its evaluated pricing lines. */
  priceData: Array<{
    quantity: string;
    pricingLines?: EvaluatedPricingLine[];
  }>;
  quantities: string[];
  isLoading: boolean;
  /** Which phase to render: COST lines sit before margin, PRICE lines after. */
  phase: PricingLinePhase;
}

/**
 * Waterfall rows for an item's evaluated pricing lines in a single phase, each
 * showing the signed delta that line adds at every quantity break. Rendered
 * between the cost and margin rows (COST phase) or between the margin and total
 * rows (PRICE phase). Returns null when the item has no lines in this phase.
 */
export function PricingLineRows({
  priceData,
  quantities,
  isLoading,
  phase,
}: PricingLineRowsProps) {
  const lineDefs = (priceData[0]?.pricingLines ?? []).filter(
    (line) => line.phase === phase,
  );
  if (lineDefs.length === 0) {
    return null;
  }

  const dataByQuantity = new Map(
    priceData.map((data) => [data.quantity, data]),
  );

  return (
    <>
      {lineDefs.map((line) => (
        <PriceRow
          key={line.name}
          label={line.description ?? line.name}
          variant="muted"
          signed
          isLoading={isLoading}
          cells={quantities.map((quantity) => ({
            quantity,
            value:
              dataByQuantity
                .get(quantity)
                ?.pricingLines?.find(
                  (candidate) => candidate.name === line.name,
                )?.delta ?? null,
          }))}
        />
      ))}
    </>
  );
}
